<template>
  <div class="substep-select-expected-contract-types">
    <div class="substep-header">
      <h3 data-testId="step-title">Who is going to track time?</h3>
      <p class="subtitle">This will help customize your Hubstaff onboarding and setup</p>
    </div>

    <div class="substep-body">
      <div class="cards">
        <label
          v-for="contractType in contractTypes"
          :key="contractType.id"
          class="card"
          :class="{
            selected: isSelected(contractType.id),
          }"
        >
          <input
            type="checkbox"
            :checked="isSelected(contractType.id)"
            @change="selectType(contractType.id)"
          />
          <div class="header">
            <div class="icon">
              <img :src="contractType.icon" />
            </div>
            <div class="name">{{ contractType.name }}</div>
          </div>
          <div
            v-if="contractType.description && !showOtherInput(contractType.id)"
            class="description"
          >
            {{ contractType.description }}
          </div>
          <input
            v-if="showOtherInput(contractType.id)"
            v-model="otherValue"
            type="text"
            class="other-input"
            placeholder="Tell us who is going to track time"
            maxlength="100"
            @blur="updateValidationState"
            @click.stop
          />
          <p
            v-if="showOtherInput(contractType.id) && hasOtherInvalidChars"
            class="other-input-error"
          >
            Invalid characters: &lt; &gt; = \ and line breaks are not allowed.
          </p>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import { OrganizationsResource } from '~/resources';

// Matches backend: prevents HTML/XML injection, injection attacks, escape issues, line breaks
const INVALID_OTHER_CHARS_REGEX = /[<>=\\\r\n]/;

export default {
  name: 'SelectExpectedContractTypes',
  components: {},
  props: {
    organization: {
      type: Object,
      required: true,
      validator: (organization) => {
        return !!organization.id;
      },
    },
    contractTypes: {
      type: Array,
      required: true,
      validator: (contractTypes) => {
        return contractTypes.every((ct) => ct.id && ct.name && ct.description && ct.icon);
      },
    },
    selectedContractTypes: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    selectedTypes: [],
    otherValue: '',
    previousValidationState: false,
  }),
  computed: {
    isValid() {
      if (this.selectedTypes.length === 0) {
        return false;
      }
      if (!this.selectedTypes.includes('other')) {
        return true;
      }

      const trimmed = this.otherValue.trim();

      return !!trimmed && !INVALID_OTHER_CHARS_REGEX.test(trimmed);
    },
    continueTooltip() {
      if (this.selectedTypes.includes('other') && !this.otherValue.trim()) {
        return 'Finish filling in the "Other" details to continue';
      }
      if (this.selectedTypes.includes('other') && INVALID_OTHER_CHARS_REGEX.test(this.otherValue.trim())) {
        return 'Remove disallowed characters (< > = \\ or line breaks) to continue';
      }

      return 'Select at least one option to continue';
    },
    hasOtherInvalidChars() {
      return (
        this.selectedTypes.includes('other') &&
        this.otherValue.trim().length > 0 &&
        INVALID_OTHER_CHARS_REGEX.test(this.otherValue.trim())
      );
    },
  },
  watch: {
    otherValue() {
      this.debouncedUpdateValidationState();
    },
    continueTooltip: {
      immediate: true,
      handler(newTooltip) {
        this.$emit('updateTooltip', newTooltip);
      },
    },
  },
  created() {
    this.debouncedUpdateValidationState = debounce(this.updateValidationState, 300);
    this.$parent.$on('userClickedContinue', this.handleFinishedSubstep);
    this.initializeSelection();
  },
  beforeDestroy() {
    this.debouncedUpdateValidationState?.cancel();
  },
  methods: {
    storedValueToDisplay(stored) {
      return String(stored ?? '').replace(/_/g, ' ');
    },
    initializeSelection() {
      const knownTypeIds = this.contractTypes.map((ct) => ct.id);
      const customType = this.selectedContractTypes.find((type) => !knownTypeIds.includes(type));

      this.selectedTypes = this.selectedContractTypes.filter((type) => knownTypeIds.includes(type));

      if (customType) {
        this.selectedTypes.push('other');
        this.otherValue = this.storedValueToDisplay(customType);
      }

      this.updateValidationState();
    },
    isSelected(typeId) {
      return this.selectedTypes.includes(typeId);
    },
    showOtherInput(contractTypeId) {
      return contractTypeId === 'other' && this.isSelected('other');
    },
    async handleFinishedSubstep() {
      const success = await this.updateExpectedContractTypes();

      if (success) {
        this.$emit('finishSubstep');
      }
    },
    selectType(type) {
      const index = this.selectedTypes.indexOf(type);

      if (index > -1) {
        this.selectedTypes.splice(index, 1);
      } else {
        this.selectedTypes.push(type);
      }

      this.updateValidationState();
    },
    updateValidationState() {
      if (this.isValid !== this.previousValidationState) {
        this.$emit(this.isValid ? 'completeSubstep' : 'incompleteSubstep');
        this.previousValidationState = this.isValid;
      }
    },
    async updateExpectedContractTypes() {
      try {
        const contractTypes = this.selectedTypes
          .map((type) => (type === 'other' && this.otherValue.trim() ? [type, this.otherValue.trim()] : type))
          .flat();

        const payload = {
          organization: {
            expected_contract_types: contractTypes,
          },
        };

        const response = await OrganizationsResource.updateExpectedContractTypes({ id: this.organization.id }, payload);

        if (response.error || response.errors) {
          const errorMessage =
            response.error ||
            (Array.isArray(response.errors) ? response.errors.join(', ') : 'Something went wrong. Please try again.');

          this.$appMessage(errorMessage);

          return false;
        }

        return true;
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }

        return false;
      }
    },
  },
};
</script>
